import { TableView } from 'api/models/TableView';
import { COMPANY_NAME_KEY } from 'common/constants/analyst-dashboards';
import { valuationsColumns } from 'dashboard409a/pages/Workboard/common/colConfig';
import { DEFAULT_VIEW_ID } from 'dashboard409a/pages/Workboard/common/constants';
import {
  ValuationView,
  WORKBOARD_META_COLUMNS,
  WorkboardTableDefinition,
} from '../types/workboardTableViews/types';

const DEFAULT_COLUMN_ORDER = valuationsColumns.map(col => col.id);

const EMPTY_TABLE_DEFINITION: WorkboardTableDefinition = {
  columns: {},
  sortKeys: [],
  groupBy: 'companies',
  filters: [],
  filterExclusions: [],
};

export const normalizeTableDefinition = (
  definition?: WorkboardTableDefinition | Record<string, unknown> | null
): WorkboardTableDefinition => {
  if (!definition || typeof definition !== 'object') {
    return { ...EMPTY_TABLE_DEFINITION };
  }

  const def = definition as Record<string, unknown>;
  return {
    columns: (def.columns as Record<number, string>) ?? {},
    sortKeys: (def.sortKeys as WorkboardTableDefinition['sortKeys']) ?? [],
    groupBy: (def.groupBy as WorkboardTableDefinition['groupBy']) ?? 'companies',
    filters: (def.filters as WorkboardTableDefinition['filters']) ?? [],
    filterExclusions: (def.filterExclusions as string[]) ?? [],
    columnWidths: (def.columnWidths as WorkboardTableDefinition['columnWidths']) ?? undefined,
  };
};

const buildColumnVisibility = (
  definition: WorkboardTableDefinition
): Record<string, boolean> => {
  const visibleColumnIds = new Set(Object.values(definition.columns));
  const hiddenColumnIds = new Set(definition.filterExclusions ?? []);
  const columnVisibility: Record<string, boolean> = {};

  DEFAULT_COLUMN_ORDER.forEach(columnId => {
    if (columnId === COMPANY_NAME_KEY) {
      return;
    }
    if (hiddenColumnIds.has(columnId)) {
      columnVisibility[columnId] = false;
      return;
    }
    if (visibleColumnIds.size > 0) {
      columnVisibility[columnId] = visibleColumnIds.has(columnId);
    }
  });

  return columnVisibility;
};

const buildColumnsFromVisibility = (
  columnVisibility: Record<string, boolean> = {}
): Pick<WorkboardTableDefinition, 'columns' | 'filterExclusions'> => {
  const visibleColumns = DEFAULT_COLUMN_ORDER.filter(columnId => {
    if (columnId === COMPANY_NAME_KEY) {
      return true;
    }
    return columnVisibility[columnId] !== false;
  });

  const columns = visibleColumns.reduce<Record<number, string>>((acc, columnId, index) => {
    acc[index] = columnId;
    return acc;
  }, {});

  const filterExclusions = DEFAULT_COLUMN_ORDER.filter(
    columnId =>
      columnId !== COMPANY_NAME_KEY && columnVisibility[columnId] === false
  );

  return { columns, filterExclusions };
};

const valuationViewToTableDefinition = (
  view: ValuationView,
  sectionId?: string
): WorkboardTableDefinition => {
  const filters: WorkboardTableDefinition['filters'] = [];

  if (sectionId) {
    filters.push([WORKBOARD_META_COLUMNS.SECTION, ['=', sectionId]]);
  }

  if (view.groupByDeals) {
    filters.push([WORKBOARD_META_COLUMNS.GROUP_BY_DEALS, ['=', 'true']]);
  }

  const { columns, filterExclusions } = buildColumnsFromVisibility(view.columnVisibility);

  return {
    columns,
    sortKeys: [],
    groupBy: 'companies',
    filters,
    filterExclusions,
  };
};

const decodeGroupByDeals = (filters: WorkboardTableDefinition['filters']): boolean =>
  filters.some(
    ([col, [, value]]) => col === WORKBOARD_META_COLUMNS.GROUP_BY_DEALS && String(value) === 'true'
  );

export const getValuationViewSectionId = (definition: WorkboardTableDefinition): string | undefined => {
  const sectionFilter = definition.filters.find(([col]) => col === WORKBOARD_META_COLUMNS.SECTION);
  if (!sectionFilter) {
    return undefined;
  }
  return String(sectionFilter[1][1]);
};

export const getDefaultValuationView = (): ValuationView => ({
  id: DEFAULT_VIEW_ID,
  name: 'Default',
  columnVisibility: {},
  groupByDeals: false,
});

export const tableDefinitionToValuationView = (
  tableView: TableView,
  definition: WorkboardTableDefinition
): ValuationView => ({
  id: tableView.id != null ? String(tableView.id) : DEFAULT_VIEW_ID,
  name: tableView.name,
  columnVisibility: buildColumnVisibility(definition),
  groupByDeals: decodeGroupByDeals(definition.filters),
  visibility: tableView.visibility,
  createdBy: tableView.created_by,
});

export const fromValuationTableView = (tableView: TableView): ValuationView => {
  const definition = normalizeTableDefinition(
    tableView.table_definition as WorkboardTableDefinition | undefined
  );
  return tableDefinitionToValuationView(tableView, definition);
};

export const toValuationTableViewCreatePayload = (
  view: ValuationView,
  sectionId?: string
): TableView => ({
  name: view.name,
  visibility: (view.visibility as TableView.visibility | undefined) ?? TableView.visibility.PRIVATE,
  table_definition: valuationViewToTableDefinition(view, sectionId),
  is_default: false,
});

export const toValuationTableViewUpdatePayload = (
  view: ValuationView,
  sectionId?: string
): Partial<TableView> => ({
  name: view.name,
  visibility: view.visibility as TableView.visibility | undefined,
  table_definition: valuationViewToTableDefinition(view, sectionId),
});

export const canEditValuationTableView = (
  view: ValuationView,
  currentUserId: number | undefined,
  isStaff: boolean
): boolean => {
  if (view.id === DEFAULT_VIEW_ID) {
    return false;
  }

  if (view.visibility === TableView.visibility.PUBLIC) {
    return isStaff || view.createdBy === currentUserId;
  }

  return view.createdBy == null || view.createdBy === currentUserId;
};
